import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLazyCreateOrderQuery } from '../redux/api/authorized/ultra';
import {
  selectTokenByNetworkAndSymbol,
  selectTokenByNetworkAndAddress,
} from '../redux/slices/tokenSlice';
import { fromTokenAmount, toTokenAmount } from './useSolana';
import { selectPrivyToken } from '../redux/slices/auth';

/**
 * Helper function to calculate a token quote (conversion between two tokens)
 *
 * @param {Object} params
 * @param {Function} params.triggerCreateOrder - Function to trigger order creation API
 * @param {Object} params.fromToken - Source token object
 * @param {Object} params.toToken - Destination token object
 * @param {string} params.amount - Amount to convert (as string)
 * @returns {Promise<string>} Formatted token amount after conversion
 */
export const calculateTokenQuote = async ({
  triggerCreateOrder,
  fromToken,
  toToken,
  amount,
}) => {
  if (!fromToken?.address || !toToken?.address || !amount) {
    return null;
  }

  // If both tokens are the same, no conversion needed
  if (fromToken.address === toToken.address) {
    return amount;
  }

  // Calculate quote via API
  const orderResult = await triggerCreateOrder({
    inputMint: fromToken.address,
    outputMint: toToken.address,
    amount: toTokenAmount(amount, fromToken.decimals),
  }).unwrap();

  if (orderResult?.outAmount && toToken.decimals) {
    return fromTokenAmount(orderResult.outAmount, toToken.decimals);
  }

  return null;
};

/**
 * Hook that handles real-time token quote calculations for amount input fields.
 * Converts between USD and target token amounts as user types.
 *
 * @param {Object} params
 * @param {string} params.network - Network name (e.g., 'solana')
 * @param {string} params.targetTokenAddress - Address of target token
 * @param {string} params.usdAmount - USD amount from form
 * @param {string} params.targetTokenAmount - Target token amount from form
 * @param {boolean} params.fromUsdcDirection - True if user is entering USD, false if entering token amount
 * @param {Function} params.setValue - Form setValue function from react-hook-form
 *
 * @returns {Object} { isLoadingQuote }
 */
export const useTokenQuote = ({
  network,
  targetTokenAddress,
  usdAmount,
  targetTokenAmount,
  fromUsdcDirection,
  setValue,
}) => {
  const privyToken = useSelector(selectPrivyToken);
  const isAuthenticated = !!privyToken;

  // Get tokens from Redux
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, network || 'solana', 'USDC'),
  );

  const targetToken = useSelector((state) =>
    targetTokenAddress && network
      ? selectTokenByNetworkAndAddress(state, network, targetTokenAddress)
      : null,
  );

  const [triggerCreateOrder] = useLazyCreateOrderQuery();
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const quoteRequestRef = useRef(null);

  /**
   * Calculate quote when USD amount changes (USD → token direction)
   */
  useEffect(() => {
    // Skip if not authenticated or not in USD direction
    if (!isAuthenticated || !fromUsdcDirection) return;
    if (!usdcToken || !targetToken) return;
    if (!usdAmount || parseFloat(usdAmount) <= 0) return;

    // Debounce quote calculation
    if (quoteRequestRef.current) {
      clearTimeout(quoteRequestRef.current);
    }

    quoteRequestRef.current = setTimeout(async () => {
      try {
        setIsLoadingQuote(true);

        // If both tokens are USDC, no conversion needed
        if (usdcToken.address === targetToken.address) {
          setValue('targetTokenAmount', usdAmount, { shouldValidate: false });
          setIsLoadingQuote(false);
          return;
        }

        // Calculate quote: USDC → target token
        const formattedTokenAmount = await calculateTokenQuote({
          triggerCreateOrder,
          fromToken: usdcToken,
          toToken: targetToken,
          amount: usdAmount,
        });

        if (formattedTokenAmount) {
          setValue('targetTokenAmount', formattedTokenAmount, {
            shouldValidate: false,
          });
        }
      } catch (error) {
        console.error('Error calculating quote:', error);
      } finally {
        setIsLoadingQuote(false);
      }
    }, 300); // Debounce 300ms

    return () => {
      if (quoteRequestRef.current) {
        clearTimeout(quoteRequestRef.current);
      }
    };
  }, [
    isAuthenticated,
    fromUsdcDirection,
    usdcToken,
    targetToken,
    usdAmount, // Only depend on source value when in USD direction
    triggerCreateOrder,
    setValue,
  ]);

  /**
   * Calculate quote when token amount changes (token → USD direction)
   */
  useEffect(() => {
    // Skip if not authenticated or in USD direction
    if (!isAuthenticated || fromUsdcDirection) return;
    if (!usdcToken || !targetToken) return;
    if (!targetTokenAmount || parseFloat(targetTokenAmount) <= 0) return;

    // Debounce quote calculation
    if (quoteRequestRef.current) {
      clearTimeout(quoteRequestRef.current);
    }

    quoteRequestRef.current = setTimeout(async () => {
      try {
        setIsLoadingQuote(true);

        // If both tokens are USDC, no conversion needed
        if (usdcToken.address === targetToken.address) {
          setValue('usdAmount', targetTokenAmount, { shouldValidate: false });
          setIsLoadingQuote(false);
          return;
        }

        // Calculate quote: target token → USDC
        const formattedUsdAmount = await calculateTokenQuote({
          triggerCreateOrder,
          fromToken: targetToken,
          toToken: usdcToken,
          amount: targetTokenAmount,
        });

        if (formattedUsdAmount) {
          setValue('usdAmount', formattedUsdAmount, { shouldValidate: false });
        }
      } catch (error) {
        console.error('Error calculating reverse quote:', error);
      } finally {
        setIsLoadingQuote(false);
      }
    }, 300); // Debounce 300ms

    return () => {
      if (quoteRequestRef.current) {
        clearTimeout(quoteRequestRef.current);
      }
    };
  }, [
    isAuthenticated,
    fromUsdcDirection,
    usdcToken,
    targetToken,
    targetTokenAmount, // Only depend on source value when in token direction
    triggerCreateOrder,
    setValue,
  ]);

  return {
    isLoadingQuote,
  };
};
